// src/bootstrap/authBootstrap.js
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

/**
 * Deterministic bootstrap:
 * - Waits for user doc (no redirect until we know role/org).
 * - Writes schoolId/orgId through to localStorage (single source of truth).
 * - Redirects only once per sign-in session (sessionStorage flag).
 */
export function installAuthBootstrap(navigate) {
  // Clear the bootstrapped flag whenever we see "signed out"
  onAuthStateChanged(auth, (u) => {
    if (!u) {
      try {
        sessionStorage.removeItem("bootstrapped");
      } catch {}
    }
  });

  // Main listener (separate so we can early return after bootstrap)
  const unsub = onAuthStateChanged(auth, async (u) => {
    // not signed in → nothing to do
    if (!u) return;

    // avoid re-routing loops within the same session
    const already = sessionStorage.getItem("bootstrapped") === "1";
    if (already) return;

    // Fetch user doc and WAIT for it
    let udoc = null;
    try {
      const snap = await getDoc(doc(db, "users", u.uid));
      udoc = snap.exists() ? snap.data() : null;
    } catch (e) {
      // If user doc fails, don't redirect; let route guards handle it
      console.warn("[bootstrap] failed to read user doc:", e?.message || e);
      sessionStorage.setItem("bootstrapped", "1");
      return;
    }

    const role = String(udoc?.role || "").toLowerCase();
    const orgId = udoc?.orgId || udoc?.schoolId || null;

    // Keep localStorage in sync for school routes
    try {
      if (orgId) localStorage.setItem("schoolId", orgId);
    } catch {}

    // Decide landing route (only when current path is a generic entry)
    const path = window.location.pathname;

    const isGenericEntry =
      path === "/" ||
      path === "/login" ||
      path === "/auth" ||
      path === "/free-dashboard"; // if it guessed free, correct it here

    if (isGenericEntry) {
      if (role === "admin") {
        navigate("/admin-dashboard", { replace: true });
      } else if ((role === "school_admin" || role === "teacher") && orgId) {
        navigate("/school-dashboard", { replace: true });
      } else if (role === "student") {
        navigate("/student-dashboard", { replace: true });
      } else if (role === "family" || role === "parent") {
        navigate("/family-dashboard", { replace: true });
      } else if (role === "personal" || role === "user") {
        navigate("/personal-dashboard", { replace: true });
      } else {
        // true fallback
        navigate("/free-dashboard", { replace: true });
      }
    }

    // mark bootstrapped for this sign-in session
    sessionStorage.setItem("bootstrapped", "1");
  });

  return unsub;
}

